import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PlayerCategoryItem,
  PlayerCategoriesData,
  getPlayerCategories,
  updatePlayerCategories,
} from '../../api/leagues';
import '../../styles/global.css';

const PlayerCategories: React.FC = () => {
  const navigate = useNavigate();
  const { leagueId, playerId } = useParams<{ leagueId: string; playerId: string }>();
  const [data, setData] = useState<PlayerCategoriesData | null>(null);
  const [assigned, setAssigned] = useState<PlayerCategoryItem[]>([]);
  const [available, setAvailable] = useState<PlayerCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('manager_token');
    if (!token || !leagueId || !playerId) {
      navigate('/manager');
      return;
    }
    localStorage.setItem('token', token);
    try {
      const result = await getPlayerCategories(leagueId, playerId);
      setData(result);
      setAssigned(result.assigned);
      setAvailable(result.available);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load categories');
    } finally {
      localStorage.removeItem('token');
      setLoading(false);
    }
  }, [leagueId, playerId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveCategories = async (newAssigned: PlayerCategoryItem[]) => {
    if (!leagueId || !playerId) return;
    setSaving(true);
    setError('');

    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');
    try {
      const result = await updatePlayerCategories(
        leagueId, playerId, newAssigned.map(c => c.id)
      );
      setData(result);
      setAssigned(result.assigned);
      setAvailable(result.available);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save categories');
    } finally {
      localStorage.removeItem('token');
      setSaving(false);
    }
  };

  const handleDragStart = (index: number, e: React.DragEvent<HTMLDivElement>) => {
    setDragIndex(index);
    dragNode.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    setTimeout(() => {
      if (dragNode.current) dragNode.current.style.opacity = '0.4';
    }, 0);
  };

  const handleDragOver = (index: number, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newList = [...assigned];
      const [moved] = newList.splice(dragIndex, 1);
      newList.splice(dragOverIndex, 0, moved);
      const updated = newList.map((item, i) => ({ ...item, level: i + 1 }));
      setAssigned(updated);
      saveCategories(updated);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragNode.current = null;
  };

  const removeCategory = (index: number) => {
    if (saving) return;
    const removed = assigned[index];
    const newAssigned = assigned.filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, level: i + 1 }));
    const newAvailable = [...available, { ...removed, level: undefined }]
      .sort((a, b) => (a.skill_level - b.skill_level) || (a.difficulty - b.difficulty) || a.name.localeCompare(b.name));
    setAssigned(newAssigned);
    setAvailable(newAvailable);
    saveCategories(newAssigned);
  };

  const addCategory = (cat: PlayerCategoryItem) => {
    if (saving) return;
    const newAssigned = [...assigned, { ...cat, level: assigned.length + 1 }];
    const newAvailable = available.filter(c => c.id !== cat.id);
    setAssigned(newAssigned);
    setAvailable(newAvailable);
    saveCategories(newAssigned);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!data) return <div className="error">Failed to load data</div>;

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">
          <button
            className="link-btn"
            onClick={() => navigate(`/manager/leagues/${leagueId}`)}
            style={{ marginRight: '10px' }}
          >
            &larr; Back
          </button>
          Flash Card Sets for {data.player_username}
        </div>
        <div className="nav-links">
          <button className="nav-btn" onClick={() => navigate('/manager/help')}>
            Help
          </button>
        </div>
      </nav>

      <div className="container">
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {/* Assigned Categories */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div className="card">
              <h3>Assigned Flash Card Sets ({assigned.length})</h3>
              {assigned.length === 0 ? (
                <p className="placeholder-text" style={{ marginTop: '10px' }}>
                  No flash card sets assigned. Add from the available list.
                </p>
              ) : (
                <div style={{ marginTop: '10px' }}>
                  {assigned.map((cat, index) => (
                    <div
                      key={cat.id}
                      draggable
                      onDragStart={(e) => handleDragStart(index, e)}
                      onDragOver={(e) => handleDragOver(index, e)}
                      onDragEnd={handleDragEnd}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid #eee',
                        gap: '8px',
                        cursor: 'grab',
                        borderTop: dragOverIndex === index && dragIndex !== null && dragIndex > index
                          ? '2px solid #007bff' : undefined,
                        borderBottomColor: dragOverIndex === index && dragIndex !== null && dragIndex < index
                          ? '#007bff' : '#eee',
                        borderBottomWidth: dragOverIndex === index && dragIndex !== null && dragIndex < index
                          ? '2px' : '1px',
                      }}
                    >
                      <span style={{ width: '30px', fontWeight: 'bold', color: '#666' }}>
                        {cat.level}
                      </span>
                      <span style={{ color: '#999', fontSize: '14px', cursor: 'grab', marginRight: '4px' }}>
                        &#x2630;
                      </span>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cat.name}
                        {cat.is_custom && (
                          <span style={{
                            marginLeft: '6px',
                            padding: '1px 6px',
                            borderRadius: '10px',
                            background: '#cce5ff',
                            color: '#004085',
                            fontSize: '10px',
                            fontWeight: 500,
                          }}>Custom</span>
                        )}
                        <span style={{ color: '#999', fontSize: '12px', marginLeft: '6px' }}>
                          D{cat.difficulty}
                        </span>
                      </span>
                      <button
                        className="btn"
                        onClick={() => navigate(`/manager/leagues/${leagueId}/categories/${cat.id}/cards`)}
                        style={{ padding: '2px 8px', fontSize: '11px', flexShrink: 0, width: '38px', textAlign: 'center' }}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => removeCategory(index)}
                        disabled={saving}
                        style={{ padding: '2px 8px', fontSize: '12px', flexShrink: 0, width: '28px', textAlign: 'center' }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Available Categories */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div className="card">
              <h3>Available Flash Card Sets ({available.length})</h3>
              {available.length === 0 ? (
                <p className="placeholder-text" style={{ marginTop: '10px' }}>
                  All flash card sets are assigned.
                </p>
              ) : (
                <div style={{ marginTop: '10px' }}>
                  {available.map((cat) => (
                    <div
                      key={cat.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid #eee',
                        gap: '8px',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cat.name}
                        {cat.is_custom && (
                          <span style={{
                            marginLeft: '6px',
                            padding: '1px 6px',
                            borderRadius: '10px',
                            background: '#cce5ff',
                            color: '#004085',
                            fontSize: '10px',
                            fontWeight: 500,
                          }}>Custom</span>
                        )}
                        <span style={{ color: '#999', fontSize: '12px', marginLeft: '6px' }}>
                          D{cat.difficulty}
                        </span>
                      </span>
                      <button
                        className="btn btn-primary"
                        onClick={() => addCategory(cat)}
                        disabled={saving}
                        style={{ padding: '2px 8px', fontSize: '12px', flexShrink: 0, width: '40px', textAlign: 'center' }}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {saving && (
          <div style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>Saving...</div>
        )}
      </div>
    </div>
  );
};

export default PlayerCategories;
